import React from "react";
import { Box, makeStyles } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";

import { PhoneNumberInput } from "../../../components/phone-number/PhoneNumberInput";
import { InputButton } from "../../../components/input-button/InputButton";
import { UserService } from "../../../services/UserService";
import { Overlay } from "../../../components/overlay/Overlay";
import { PhoneNumber } from "libphonenumber-js/types";
import { PublicFaqsList } from "../../public/PublicFaqsList";
import { Link } from "react-router-dom";

interface CheckPhoneNumberProps {
  callback: (isoCode: string, dialCode: string, phone: string) => void;
}

export interface FormRequestState {
  telephone: string;
  dial_code: string;
  country_iso_code: string;
}

export const useStyles = makeStyles(
  (theme) => ({
    faqWrapper: {
      width: "100%",
      textAlign: "center",
      paddingTop: 15,
      "& a": {
        width: "100%",
        color: theme.palette.secondary.main,
        textDecoration: "none",
        fontSize: 14,
        fontWeight: theme.typography.fontWeightBold,

        "&:hover": {
          textDecoration: "underline",
        },
      },
    },
  }),
  {
    name: "check-phone-number",
    flip: null,
  }
);

export const CheckPhoneNumber = (props: CheckPhoneNumberProps) => {
  const classes = useStyles(props);
  const { formatMessage } = useIntl();

  const handlePhoneChange = (phoneNumber: PhoneNumber | null) => {
    if (phoneNumber !== null) {
      formik.values.telephone = phoneNumber.number.toString();
      formik.values.dial_code = `+${phoneNumber.countryCallingCode}`;
      formik.values.country_iso_code = phoneNumber.country
        .toString()
        .toUpperCase();

      if (!phoneNumber.isValid()) {
        formik.setErrors({
          ...formik.errors,
          telephone: formatMessage({ id: "error.invalidPhoneNumber" }),
        });
      } else {
        formik.setErrors({});
      }
    } else {
      formik.values.telephone = "";
      formik.setErrors({});
    }
  };

  const formik = useFormik({
    initialValues: {
      telephone: "",
      country_iso_code: "",
      dial_code: "",
    },
    validationSchema: Yup.object().shape({
      telephone: Yup.string().required(
        formatMessage({ id: "error.enterValidPhoneNumber" })
      ),
      country_iso_code: Yup.string()
        .max(2, formatMessage({ id: "error.invalidCountryIsoCode" }))
        .required(formatMessage({ id: "error.invalidCountryIsoCode" })),
    }),
    onSubmit: async (
      values: FormRequestState,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await UserService.sendOtp({
          telephone: values.telephone,
          country_iso_code: values.country_iso_code,
        });
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 150: // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            case 106: // Unsupported country
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            case 105: // Number of issue exceeded
              setErrors({ unknown: response.data.message });
              break;
            case 100: // Ok
              // Call main screen callback and pass it country and phone number
              props.callback(
                formik.values.country_iso_code,
                formik.values.dial_code,
                formik.values.telephone
              );
              break;
          }
        } else {
          setErrors({
            unknown: formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (err) {
        setSubmitting(false);
        setErrors({
          unknown: formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const phoneNumberError = () => {
    if (formik.errors.telephone) {
      return formik.errors.telephone as string;
    }

    if (formik.errors.country_iso_code) {
      return formik.errors.country_iso_code as string;
    }

    return "";
  };

  return (
    <>
      <Box mb={4} component="span" className="title">
        <FormattedMessage id="sharedScreen.signin" />
      </Box>
      {formik.errors.unknown ? (
        <Box mt={2} mb={2} component="span" className="errorWrapper">
          {formik.errors.unknown as string}
        </Box>
      ) : (
        <></>
      )}

      <form onSubmit={formik.handleSubmit}>
        <PhoneNumberInput
          name="telephone"
          label={formatMessage({ id: "sharedScreen.phoneNumber" })}
          error={phoneNumberError()}
          onChange={handlePhoneChange}
        />
        <Box marginTop={3}>
          <InputButton
            color="secondary"
            fullWidth
            size="medium"
            variant="contained"
            type="submit"
            disabled={Boolean(formik.errors.telephone)}
          >
            <FormattedMessage id="sharedScreen.next" />
          </InputButton>
          
          <Box
            className={classes.faqWrapper}
            display="flex"
            alignContent="center"
            justifyContent="space-between"
          >
            <Link to={`${PublicFaqsList.routeName}`} target={"blank"}>
                <FormattedMessage id="sharedScreen.accessFaq" />
            </Link>
          </Box>
        </Box>
      </form>

      <Overlay loading={formik.isSubmitting} />
    </>
  );
};
